import React, { useState, useEffect } from "react";
import { Link } from "react-scroll";
import { motion } from "framer-motion";
import "../../assets/styles/home.css";
import SampleVideo from "../../assets/images/animate.mp4";
import { FaSearch } from "react-icons/fa";
import Logo from "../../assets/images/new.png";
import { Link as RouterLink } from "react-router-dom";

const Home = () => {
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);

  const handleNavbarToggle = () => {
    setIsNavbarOpen((prev) => !prev);

    const navbar = document.getElementById("navbarNav");
    if (navbar) {
      if (!isNavbarOpen) {
        navbar.classList.add("show");
      } else {
        navbar.classList.remove("show");
      }
    }
    console.log("Navbar Open State:", isNavbarOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const navbar = document.getElementById("navbarNav");
      const toggleButton = document.querySelector(".navbar-toggler");

      if (
        isNavbarOpen &&
        navbar &&
        !navbar.contains(event.target) &&
        !toggleButton.contains(event.target)
      ) {
        setIsNavbarOpen(false);
        navbar.classList.remove("show");
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isNavbarOpen]);

  const handleSetActiveSection = (section) => setActiveSection(section);

  const handleScroll = () => {
    const sections = [
      "company-section",
      "product-section",
      "solutions-section",
      "brand-section",
      "the hub-section",
      "contact us-section",
    ];

    let currentSection = ""; // Default to home if no section is active
    let minDistance = window.innerHeight;

    sections.forEach((id) => {
      const section = document.getElementById(id);
      if (section) {
        const rect = section.getBoundingClientRect();
        const distanceFromTop = Math.abs(rect.top);

        if (distanceFromTop < minDistance) {
          minDistance = distanceFromTop;
          currentSection = id.replace("-section", ""); // Remove '-section' to match state keys
        }
      }
    });

    setActiveSection(currentSection);

    // Handle navbar background change on scroll
    setIsScrolled(window.scrollY > 50);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Animation Variants
  const containerVariant = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3, delayChildren: 0.5 },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const videoVariant = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.5, ease: "easeOut" },
    },
  };

  return (
    <motion.div
      className="hero-section text-white"
      initial="hidden"
      animate="visible"
      variants={containerVariant}
    >
      {/* Navbar */}
      <motion.nav
        className={`navbar navbar-expand-lg navbar-dark ${
          isScrolled ? "scrolled" : ""
        }`}
        style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000 }}
        variants={itemVariant}
      >
        <div className="container-fluid d-flex justify-content-between align-items-center">
          {/* Logo */}
          <motion.a
            className="navbar-brand ms-3"
            href="#"
            style={{ fontSize: "18px", fontWeight: 600 }}
            variants={itemVariant}
          >
            <img
              src={Logo}
              alt="Logo"
              style={{ height: "60px", objectFit: "contain" }}
              className="logo"
            />
          </motion.a>

          {/* Navbar items and search icon container */}
          <div className="nav-items-container">
            <button
              className="navbar-toggler"
              type="button"
              onClick={handleNavbarToggle}
              aria-expanded={isNavbarOpen ? "true" : "false"}
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <motion.div
              className={`collapse navbar-collapse justify-content-center ${
                isNavbarOpen ? "show" : ""
              }`}
              variants={containerVariant}
            >
              <ul className="navbar-nav">
                {[
                  "company",
                  "product",
                  "solutions",
                  "brand",
                  "the hub",
                  "contact us",
                ].map((section) => (
                  <motion.li
                    className="nav-item"
                    key={section}
                    variants={itemVariant}
                  >
                    {section === "solutions" ? (
                      // Use react-router-dom's Link for the solutions section
                      <RouterLink
                        className={`nav-link ${
                          activeSection === section ? "active" : ""
                        }`}
                        to="/solutions" 
                        onClick={() => {
                          setIsNavbarOpen(false);
                          document.getElementById("navbarNav")?.classList.remove("show");
                        }}
                      >
                        {section.toUpperCase()}
                      </RouterLink>
                    ) : (
                      // Keep using react-scroll for other sections
                      <Link
                        className={`nav-link ${
                          activeSection === section ? "active" : ""
                        }`}
                        to={`${section}-section`}
                        smooth={true}
                        offset={-100}
                        duration={300}
                        onClick={() => {
                          setIsNavbarOpen(false);
                          document.getElementById("navbarNav")?.classList.remove("show");
                        }}
                      >
                        {section.toUpperCase()}
                      </Link>
                    )}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Search Icon */}
            <motion.div
              className="search-icon me-3 d-none d-lg-block"
              variants={itemVariant}
            >
              <FaSearch />
            </motion.div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Content */}
      <motion.div
        className="hero-content text-center"
        initial="hidden"
        animate="visible"
        variants={containerVariant}
      >
        {/* Video Background */}
        <motion.video
          className="video-background"
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: -1,
          }}
          variants={videoVariant}
        >
          <source src={SampleVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </motion.video>

        {/* Text Overlay */}
        <motion.div className="text-overlay" variants={containerVariant}>
          <motion.h1
            className="display-4  custom-headinghome"
            variants={itemVariant}
          >
            Every Challenge Deserves
            <br /> A Custom Solution
          </motion.h1>
          <motion.p
            className="lead custom-paragraphhome"
            variants={itemVariant}
          >
            Each brand of ours curates its inventory to deliver innovative,
            flexible and
            <br /> responsive solutions ensuring we meet your unique needs with
            precision
            <br /> and expertise.
          </motion.p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Home;
